import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatDetail } from "@/lib/api";
import { Trophy, ScrollText, Loader2, RefreshCw } from "lucide-react";

export function AgentLeaderboardAdmin() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/agent-leaderboard");
      setRows(data.leaderboard || []);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="mv-card p-5" data-testid="admin-agent-leaderboard">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Trophy size={16} className="mv-green-text"/>
          <div className="font-display text-[16px] mv-green-text">Agent leaderboard</div>
        </div>
        <button type="button" onClick={load} disabled={busy} className="btn-ghost" data-testid="admin-leaderboard-refresh">
          {busy ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>} Refresh
        </button>
      </div>
      {rows === null ? (
        <div className="text-[13px]" style={{ color: "var(--mv-muted)" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[13px]" style={{ color: "var(--mv-muted)" }}>No agent activity yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left" style={{ color: "var(--mv-muted)" }}>
                <th className="py-2 pr-4">#</th>
                <th className="py-2 pr-4">Agent</th>
                <th className="py-2 pr-4">Code</th>
                <th className="py-2 pr-4">Clicks</th>
                <th className="py-2 pr-4">Registrations</th>
                <th className="py-2">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.code || i} className="border-t mv-hairline" data-testid={`admin-lb-row-${r.code || 'unknown'}`}>
                  <td className="py-2 pr-4 font-mono">{i + 1}</td>
                  <td className="py-2 pr-4">{r.full_name || <span style={{ color: "var(--mv-muted)" }}>— unknown —</span>}</td>
                  <td className="py-2 pr-4 font-mono">{r.code || "—"}</td>
                  <td className="py-2 pr-4">{r.clicks}</td>
                  <td className="py-2 pr-4">{r.registrations}</td>
                  <td className="py-2">{r.last_activity ? new Date(r.last_activity).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AuditLogAdmin() {
  const [rows, setRows] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/admin/audit-log?limit=200"); setRows(data.entries || []); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="mv-card p-5" data-testid="admin-audit-log">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ScrollText size={16} className="mv-green-text"/>
          <div className="font-display text-[16px] mv-green-text">Admin audit log</div>
        </div>
        <button type="button" onClick={load} className="btn-ghost" data-testid="admin-audit-refresh">
          <RefreshCw size={14}/> Refresh
        </button>
      </div>
      {rows === null ? (
        <div className="text-[13px]" style={{ color: "var(--mv-muted)" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[13px]" style={{ color: "var(--mv-muted)" }}>No admin actions logged yet.</div>
      ) : (
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-[13px]">
            <thead className="sticky top-0 bg-white">
              <tr className="text-left" style={{ color: "var(--mv-muted)" }}>
                <th className="py-2 pr-4">When</th>
                <th className="py-2 pr-4">Actor</th>
                <th className="py-2 pr-4">Action</th>
                <th className="py-2">Target / meta</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t mv-hairline align-top">
                  <td className="py-2 pr-4 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="py-2 pr-4">{r.actor_email}</td>
                  <td className="py-2 pr-4 font-mono text-[12px]">{r.action}</td>
                  <td className="py-2">
                    <div className="text-[12.5px]">{r.target || "—"}</div>
                    {r.meta && Object.keys(r.meta).length > 0 && (
                      <div className="text-[11.5px] font-mono" style={{ color: "var(--mv-muted)" }}>
                        {JSON.stringify(r.meta)}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
